<template>
  <form @submit.prevent="handleUpdateProfile()" data-cy="profile-update-form">
    <ion-card>
      <ion-card-header class="ion-no-padding-bottom">
        <ion-card-title>Public Profile Information</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <p v-if="messagingForRunner">
          Customers will see this if you're running an order for them.
        </p>
        <ion-list>
          <input-box
            :validator="$v.displayName"
            :error-messages="{
              required: 'Please enter a name.',
              maxLength: 'Please limit to 255 characters.'
            }"
            :required="displayNameRequired"
            data-cy="display-name"
            v-model="displayName"
            maxlength="255"
          >
            Name
          </input-box>
          <ion-item v-if="showAvatar" lines="none">
            <ion-label
              position="stacked"
              :color="$v.newAvatar.$error ? 'danger' : ''"
            >
              My Avatar
            </ion-label>
          </ion-item>
          <ion-item v-if="showAvatar">
            <ion-avatar slot="start">
              <img :src="user.photoURL" alt="My Avatar" class="avatar" />
            </ion-avatar>
            <ion-button
              v-if="!pickingNewAvatar"
              size="small"
              color="light"
              @click="enablePickNewAvatar()"
              >Choose another</ion-button
            >
            <div v-else class="w-100">
              <ion-grid>
                <ion-row>
                  <ion-col size="9">
                    <ion-input
                      ref="avatar"
                      type="file"
                      accept="image/jpeg, image/png"
                      @change="handleAvatarChanged"
                    />
                  </ion-col>
                  <ion-col size="3">
                    <a
                      href="#"
                      @click.prevent="disablePickNewAvatar()"
                      class="ion-float-right"
                      >cancel</a
                    >
                  </ion-col>
                </ion-row>
              </ion-grid>
            </div>
          </ion-item>
          <ion-item
            v-if="$v.newAvatar.$error && !$v.newAvatar.imageUploadSizeMax"
            color="danger"
            >The maximum file upload size is limited to 1MB.</ion-item
          >
          <ion-item
            v-if="$v.newAvatar.$error && !$v.newAvatar.imageDimensionsAreSquare"
            color="danger"
            >The image must be exactly square.</ion-item
          >
        </ion-list>
      </ion-card-content>
    </ion-card>
    <ion-list lines="full">
      <input-box
        :validator="$v.email"
        :error-messages="{
          required: 'Please enter an email address.',
          email: 'Please enter a valid email address.'
        }"
        :required="true"
        data-cy="email"
        type="email"
        v-model="email"
      >
        Email
      </input-box>
    </ion-list>

    <div class="ion-padding">
      <submit-button :submitting="submitting" data-cy="profile-update-submit">
        Save
      </submit-button>
    </div>
  </form>
</template>

<script>
/**
 * This has an insane amount of repeated code with the Edit user. Look at it's notes for why
 */

import { required, email, requiredIf } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";
import ErrorAlert from "@/ErrorAlert";
import {
  imageUploadSizeMax,
  imageDimensionsAreSquare
} from "@/validators/validators";
import ImageMixin from "@/mixins/image";
import submittable from "@/mixins/submittable";
import maxLength from "vuelidate/lib/validators/maxLength";

export default {
  mixins: [ImageMixin, submittable],

  props: {
    displayNameRequired: {
      required: true,
      type: Boolean
    },

    showAvatar: {
      required: true,
      type: Boolean
    },

    messagingForRunner: {
      required: true,
      type: Boolean
    }
  },

  data: function() {
    return {
      displayName: "",
      email: "",
      pickingNewAvatar: false,
      newAvatar: ""
    };
  },

  created() {
    this.displayName = this.user.displayName;
    this.email = this.user.email;
  },

  methods: {
    ...mapActions("auth", [
      "updateProfile",
      "updateEmail",
      "authThenUpdateEmail"
    ]),

    /**
     * It's important to understand this: we do these two updates one after another.  The reason is
     * that the email is probably the least actively changed, plus it may require a re-auth, so we
     * might have to issue a modal as well
     */
    async handleUpdateProfile() {
      this.$v.$touch();

      // right now touch is not promisified - I couldn't get it to work well anyway
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!this.$v.$invalid) {
        // lets only do this if it's changed
        if (this.displayName !== this.user.displayName || this.newAvatar) {
          let updateParams = {};

          if (this.newAvatar) {
            let ref = this.$storage().ref();
            let uploadRef = ref.child(this.generateUserImagePath(this.user));

            const uploadTaskSnapshot = await uploadRef.put(this.newAvatar, {
              contentType: this.newAvatar.type
            });

            const downloadURL = await uploadTaskSnapshot.ref.getDownloadURL();
            updateParams.photoURL = downloadURL;
          }

          if (this.displayName !== this.user.displayName) {
            updateParams.displayName = this.displayName;
          }

          let result = await this.updateProfile(updateParams);

          if (result === false) {
            ErrorAlert(this.authError.message);
            return false;
          } else {
            this.disablePickNewAvatar();
          }
        }

        // same with this, only do it if its changed
        if (this.email !== this.user.email) {
          let result = await this.updateEmail({ email: this.email });

          if (result === false) {
            if (this.authError.code === "auth/requires-recent-login") {
              this.promptForPasswordConfirm();
            } else {
              ErrorAlert(this.authError.message);
            }
            return false;
          }
        }

        this.successToast("Your profile has been updated successfully.");
      }
    },

    promptForPasswordConfirm() {
      this.$ionic.alertController
        .create({
          header: "Updating Email",
          subHeader: "Please confirm your password",
          message:
            "Changing your email address requires you to confirm your password.",
          inputs: [
            {
              name: "confirmPassword",
              type: "password",
              placeholder: "Type your password"
            }
          ],
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
              cssClass: "secondary"
            },
            {
              text: "Ok",
              cssClass: "success",
              handler: alertData => {
                if (alertData.confirmPassword == "") {
                  return false; // this is kinda hacky but it stops them from submitting a blank password
                }
                this.handleUpdateEmailWithPassword(alertData.confirmPassword);
              }
            }
          ]
        })
        .then(a => a.present());
    },

    async handleUpdateEmailWithPassword(password) {
      let result = await this.authThenUpdateEmail({
        password,
        email: this.email
      });

      if (result === false) {
        let errorMessage = this.authError.message;
        if (this.authError.code === "auth/wrong-password") {
          errorMessage = "That password is incorrect.";
        }

        ErrorAlert(errorMessage);
        return false;
      }

      this.successToast("Your profile has been updated successfully.");
    },

    enablePickNewAvatar() {
      this.pickingNewAvatar = true;

      // this is kind of hacky to get this to auto pop the element
      setTimeout(() => {
        document.querySelector("input[type=file]").click();
      }, 100); // next tick was too fast I guess
    },

    disablePickNewAvatar() {
      this.pickingNewAvatar = false;
      this.newAvatar = "";
    },

    handleAvatarChanged(event) {
      let files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        return;
      }
      this.newAvatar = files[0];
      this.$v.newAvatar.$reset();
    }
  },

  computed: {
    ...mapGetters("auth", ["user", "authError"])
  },

  validations: {
    displayName: {
      required: requiredIf(self => {
        return self.displayNameRequired;
      }),
      maxLength: maxLength(255)
    },

    email: {
      required,
      email
    },

    /**
     * Remember, this only works because we're doing web-only
     * In the future if this is for mobile, we'd need to get that working with a plugin
     * Also we're going to rely on the browser for validation - because these are admin
     */
    newAvatar: {
      imageUploadSizeMax,
      imageDimensionsAreSquare
    }
  }
};
</script>
